import React, { useEffect, useState } from 'react'

import classNames from 'clsx'
import colorUtil from 'color'
import PropTypes from 'prop-types'
import { useConnect } from 'redux-bundler-hook'

import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import {
  Drawer,
  IconButton,
  Portal,
  Tooltip,
} from '@mui/material'
import { makeStyles } from '@mui/styles'

import { dasherize } from 'inflection'

import createLogger from '~/src/Lib/Logging'
import { defer, EMPTY_ARRAY } from '~/src/Lib/Utils'

import { Allowed } from '../Flags'

const ACTION_GAP = 16
const DRAWER_WIDTH = 405

const displayName = 'Drawer'
const logger = createLogger(displayName)

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    marginLeft: ACTION_GAP,
    height: '100%',
    overflowX: 'hidden',
    '#wrapper &': {
      marginLeft: 0,
    }
  },
  drawerPaper: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: ACTION_GAP,
    width: DRAWER_WIDTH,
    height: 'auto',
    overflow: 'visible',
    background: theme.palette.background.dark,
    border: `1px solid ${theme.palette.appPrimary.active}`,
    borderWidth: '1px 0 0 1px',
    transition: theme.transitions.create(['borderColor'], {
      duration: theme.transitions.duration.enteringScreen,
      easing: theme.transitions.easing.easeInOut,
    }),
    '&.aroya__drawer__open': {
      borderColor: theme.palette.appPrimary.disabled,
      transition: theme.transitions.create(['borderColor'], {
        duration: theme.transitions.duration.leavingScreen,
        easing: theme.transitions.easing.easeInOut,
      }),
    },
  },
  actions: {
    position: 'absolute',
    top: '6.25rem',
    left: -ACTION_GAP,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    visibility: 'visible',
    zIndex: 1,
  },
  tabButton: {
    backgroundColor: theme.palette.background.dark,
    border: `1px solid ${theme.palette.primary.disabled ?? theme.palette.blue.disabled}`,
    boxShadow: `0 0 2px ${theme.palette.primary.disabled ?? theme.palette.blue.disabled}`,
    minWidth: '2rem',
    minHeight: '2rem',
    width: '2rem',
    height: '2rem',
    '&:hover': {
      backgroundColor: colorUtil(theme.palette.background.dark)
        .mix(colorUtil(theme.palette.contrast.main), theme.palette.states.enabled)
        .string(),
    },
    '& [class*=MuiSvgIcon-root]': {
      fontSize: '1.4rem',
    },
  },
  tabSelected: {
    color: theme.palette.text.primary,
  },
  content: {
    height: '100%',
    display: 'flex',
  },
}), { name: displayName })

const TabbedDrawer = ({
  open,
  tab = null,
  tabs,
  onClose,
  onOpen,
}) => {
  const classes = useStyles(({ open }))
  const [hoveredTabKey, setHoveredTabKey] = useState(null)
  const selectedTab = tabs.find(t => t.key === tab)

  const openDrawer = tabKey => {
    selectedTab?.onClose?.()
    const nextTab = tabs.find(t => t.key === tabKey)
    if (nextTab && typeof nextTab.onOpen === 'function') {
      nextTab.onOpen()
    }
    onOpen(tabKey)
  }

  const closeDrawer = () => {
    if (selectedTab && typeof selectedTab.onClose === 'function') {
      selectedTab.onClose()
    }
    onClose()
  }

  const handleClickTabIcon = tabKey => {
    if (open && tabKey === tab) {
      closeDrawer()
    } else {
      openDrawer(tabKey)
    }
  }

  if (open && !selectedTab) {
    closeDrawer()
  }

  useEffect(() => () => {
    closeDrawer()
  // disabled lint rule because we only want to close the drawer when the component unmounts
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Allowed apps="AROYA">
      <Drawer
        classes={{
          root: classes.root,
          paper: classes.drawerPaper
        }}
        data-testid="aroya-drawer"
        anchor="right"
        open={open}
        variant="permanent"
        PaperProps={{
          'data-time-series__cursor-exclude': true,
          className: open ? 'aroya__drawer__open' : undefined,
        }}
        sx={theme => ({
          width: (open ? DRAWER_WIDTH + ACTION_GAP : '3rem'),
          transition: (open ? (
            theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            })
          ) : (
            theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            })
          )),
          '& .aroya__drawer__content': {
            opacity: (open ? 1 : 0),
            transition: (open ? (
              theme.transitions.create('opacity', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              })
            ) : (
              theme.transitions.create('opacity', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              })
            )),
          },
        })}
      >
        <div className={classes.actions}>
          {tabs.map(({ Icon, key, tooltip }) => {
            const isSelected = open && key === tab
            const showCloseIcon = isSelected && key === hoveredTabKey
            return (
              <Tooltip
                key={key}
                placement="left"
                title={isSelected ? 'Close' : tooltip}
              >
                <IconButton
                  className={classNames({ [classes.tabButton]: true, [classes.tabSelected]: isSelected })}
                  data-testid={`drawer-tab-${dasherize(key.toLowerCase())}`}
                  data-time-series__cursor-exclude
                  color="primary"
                  size="small"
                  onClick={() => handleClickTabIcon(key)}
                  onMouseEnter={() => isSelected && setHoveredTabKey(key)}
                  onMouseLeave={() => setHoveredTabKey(null)}
                >
                  {showCloseIcon ? <ChevronRightIcon /> : <Icon />}
                </IconButton>
              </Tooltip>
            )
          })}
        </div>
        <div className={classNames(classes.content, 'aroya__drawer__content')}>
          {selectedTab?.content}
        </div>
      </Drawer>
    </Allowed>
  )
}

TabbedDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  tab: PropTypes.string,
  tabs: PropTypes.arrayOf(PropTypes.shape({
    Icon: PropTypes.elementType.isRequired,
    content: PropTypes.node.isRequired,
    key: PropTypes.string.isRequired,
    tooltip: PropTypes.string.isRequired,
    onClose: PropTypes.func,
    onOpen: PropTypes.func,
  })).isRequired,
  onClose: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
}

const AppDrawer = ({
  renderOnMount = '',
  open = null,
  tabs = EMPTY_ARRAY,
  tab = null,
  onClose = null,
  onOpen = null,
}) => {
  const {
    drawer,
    routeInfo,
    doMountDrawer,
    doUnmountDrawer,
    doRenderDrawerTab,
    doOpenDrawer,
    doCloseDrawer,
    doUpdateDimensions,
  } = useConnect(
    'selectDrawer',
    'selectRouteInfo',
    'doMountDrawer',
    'doUnmountDrawer',
    'doRenderDrawerTab',
    'doOpenDrawer',
    'doCloseDrawer',
    'doUpdateDimensions',
  )

  useEffect(() => {
    if (renderOnMount) {
      doRenderDrawerTab(renderOnMount)
    }
    doMountDrawer()
    return () => doUnmountDrawer()
  }, [doMountDrawer, doRenderDrawerTab, doUnmountDrawer, renderOnMount])

  useEffect(() => {
    // Triggers the facility dashboard's map view to re-center
    defer(() => doUpdateDimensions(), 400)
  }, [doUpdateDimensions, drawer.isOpen])

  return (
    <Portal container={() => document.getElementById('drawerRoot')}>
      <TabbedDrawer
        open={open ?? drawer.isOpen}
        tab={tab ?? drawer.tab}
        tabs={tabs}
        onClose={() => {
          doCloseDrawer()
          if (typeof onClose === 'function') onClose()
        }}
        onOpen={tabKey => {
          doRenderDrawerTab(tabKey)
          doOpenDrawer()
          if (typeof onOpen === 'function') onOpen()
        }}
        routeInfo={routeInfo}
      />
    </Portal>
  )
}

AppDrawer.propTypes = {
  open: PropTypes.bool,
  renderOnMount: PropTypes.string,
  tab: PropTypes.string,
  tabs: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  onClose: PropTypes.func,
  onOpen: PropTypes.func,
}

export default AppDrawer
